import { get as getFromStorage, remove as removeFromStorage, set as setInStorage } from 'lib/storage/isomorphicLocalStorage'
import { isOidcProvider, OidcProvider } from './providers'

// Authoritative signal for "was this an Auth V2 login" — NOT the presence of an
// oidc.user:* key, which can linger after a stale v2 session and would misroute a
// v1 logout to the end-session endpoint. Free of any oidc-client-ts import so the
// always-loaded login choke points don't pull the library into the main bundle.
export const AUTH_METHOD_STORAGE_KEY = 'auth_method'
export const OIDC_PROVIDER_STORAGE_KEY = 'auth_oidc_provider'
export const OIDC_PENDING_PROVIDER_STORAGE_KEY = 'auth_oidc_pending_provider'
export const OIDC_AUTH_METHOD = 'oidc'

// All three providers share one redirect URI, so the choice has to survive the
// round trip to svc-auth for the callback to rebuild the matching client.
export const setPendingOidcProvider = (provider: OidcProvider): void => {
  setInStorage(OIDC_PENDING_PROVIDER_STORAGE_KEY, provider)
}

export const takePendingOidcProvider = (): OidcProvider | undefined => {
  const provider = getFromStorage(OIDC_PENDING_PROVIDER_STORAGE_KEY)
  removeFromStorage(OIDC_PENDING_PROVIDER_STORAGE_KEY)
  return isOidcProvider(provider) ? provider : undefined
}

// The provider is stored alongside the method because logout and silent renew
// need the client that issued the session.
export const markOidcSession = (provider: OidcProvider): void => {
  setInStorage(AUTH_METHOD_STORAGE_KEY, OIDC_AUTH_METHOD)
  setInStorage(OIDC_PROVIDER_STORAGE_KEY, provider)
}

export const clearAuthMethodMarker = (): void => {
  removeFromStorage(AUTH_METHOD_STORAGE_KEY)
  removeFromStorage(OIDC_PROVIDER_STORAGE_KEY)
}

export const isOidcSession = (): boolean => getFromStorage(AUTH_METHOD_STORAGE_KEY) === OIDC_AUTH_METHOD

export const getOidcSessionProvider = (): OidcProvider | undefined => {
  const provider = getFromStorage(OIDC_PROVIDER_STORAGE_KEY)
  return isOidcProvider(provider) ? provider : undefined
}

interface LoginSuccessAction {
  source?: App.SignUpAuthSource
  data?: {
    authMethod?: typeof OIDC_AUTH_METHOD
    oidcProvider?: OidcProvider
  }
}

// A v2 login sets the marker; a fresh v1 sign-in (has `source`) clears it; a
// session-restore dispatch (initialiseUser — no `source`) leaves it untouched.
// That last case matters: the v2 callback reloads the app, and the reload's
// restore dispatch must not wipe the marker or logout takes the v1 path.
export const syncAuthMethodMarkerFromLogin = (action: LoginSuccessAction): void => {
  const { authMethod, oidcProvider } = action.data ?? {}

  if (authMethod === OIDC_AUTH_METHOD && oidcProvider) {
    markOidcSession(oidcProvider)
  } else if (action.source) {
    clearAuthMethodMarker()
  }
}
